import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, AlertCircle, ChevronDown, ChevronRight, Pencil, Check, X } from "lucide-react";

const LABELS = ["averse", "cautious", "open", "hungry"];
const STATUS = {
  breached: { label: "Breached", cls: "bg-red-100 text-red-700", border: "border-l-red-500", icon: AlertTriangle },
  approaching: { label: "Approaching", cls: "bg-amber-100 text-amber-700", border: "border-l-amber-400", icon: AlertCircle },
  within: { label: "Within appetite", cls: "bg-emerald-100 text-emerald-700", border: "border-l-emerald-500", icon: ShieldCheck },
};
const gbp = (v) => `£${new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0)}`;
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const SummaryTile = ({ count, status, testid }) => {
  const cfg = STATUS[status];
  const Icon = cfg.icon;
  return (
    <div data-testid={testid} className="flex items-center gap-3 rounded-md border border-black/5 bg-white px-5 py-4">
      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${cfg.cls}`}><Icon size={18} /></span>
      <div>
        <p className="text-2xl font-bold text-ek-ink">{count}</p>
        <p className="text-xs text-ek-ink/65">{cfg.label}</p>
      </div>
    </div>
  );
};

const Editor = ({ item, onSaved, onCancel }) => {
  const [label, setLabel] = useState(item.appetite_label);
  const [tolRes, setTolRes] = useState(item.tolerance_residual);
  const [tolAle, setTolAle] = useState(item.tolerance_ale_gbp);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/appetite/${item.theme_id}`, {
        appetite_label: label, tolerance_residual: Number(tolRes), tolerance_ale_gbp: Number(tolAle),
      });
      toast.success(`Appetite updated for ${item.theme_name}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 rounded-md border border-ek-purple/20 bg-ek-purple/5 p-4 sm:grid-cols-4" data-testid="appetite-editor">
      <div>
        <label className="text-[0.7rem] font-semibold text-ek-ink/65">Appetite</label>
        <Select value={label} onValueChange={setLabel}>
          <SelectTrigger data-testid="appetite-label-select" className="mt-1 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{LABELS.map((l) => <SelectItem key={l} value={l}>{cap(l)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[0.7rem] font-semibold text-ek-ink/65">Max residual (1-25)</label>
        <input type="number" min="1" max="25" value={tolRes} onChange={(e) => setTolRes(e.target.value)}
          data-testid="appetite-tolres-input"
          className="mt-1 w-full rounded border border-black/15 px-2 py-1.5 text-xs outline-none focus:border-ek-purple" />
      </div>
      <div>
        <label className="text-[0.7rem] font-semibold text-ek-ink/65">Financial tolerance (£ ALE)</label>
        <input type="number" min="0" step="100000" value={tolAle} onChange={(e) => setTolAle(e.target.value)}
          data-testid="appetite-tolale-input"
          className="mt-1 w-full rounded border border-black/15 px-2 py-1.5 text-xs outline-none focus:border-ek-purple" />
      </div>
      <div className="flex items-end gap-2">
        <button onClick={save} disabled={saving} data-testid="appetite-save" className="ek-btn flex-1 py-1.5 text-xs">
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="rounded border border-black/15 px-3 py-1.5 text-ek-ink/65 hover:text-red-600"><X size={14} /></button>
      </div>
    </div>
  );
};

const ThemeCard = ({ item, canWrite, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const cfg = STATUS[item.status];
  const aleOver = item.ale_breach;
  return (
    <div data-testid={`appetite-card-${item.theme_id}`} className={`rounded-md border border-black/5 border-l-4 bg-white p-5 ${cfg.border}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg text-ek-ink">{item.theme_name}</h3>
            <span className="rounded-full bg-ek-ink/5 px-2 py-0.5 text-[0.65rem] font-semibold uppercase text-ek-ink/60">{cap(item.appetite_label)}</span>
          </div>
          <p className="mt-0.5 text-xs text-ek-ink/65">{item.risk_count} active risks in theme</p>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid={`status-${item.theme_id}`} className={`rounded-full px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase ${cfg.cls}`}>{cfg.label}</span>
          {canWrite && !editing && (
            <button onClick={() => setEditing(true)} data-testid={`edit-appetite-${item.theme_id}`} className="text-ek-ink/30 hover:text-ek-purple"><Pencil size={14} /></button>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Tolerance (max residual)</p>
          <p className="text-lg font-semibold text-ek-ink">{item.tolerance_residual}</p>
        </div>
        <div>
          <p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Current max residual</p>
          <p className={`text-lg font-semibold ${item.max_residual > item.tolerance_residual ? "text-red-600" : "text-ek-ink"}`}>
            {item.max_residual}
            {item.residual_headroom < 0 ? <span className="ml-1 text-xs">(+{-item.residual_headroom})</span> : null}
          </p>
        </div>
        <div>
          <p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Financial tolerance</p>
          <p className="text-lg font-semibold text-ek-ink">{item.tolerance_ale_gbp ? gbp(item.tolerance_ale_gbp) : "—"}</p>
        </div>
        <div>
          <p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Theme ALE</p>
          <p className={`text-lg font-semibold ${aleOver ? "text-red-600" : "text-ek-ink"}`}>{gbp(item.theme_ale_gbp)}</p>
        </div>
      </div>

      {item.breaching_risks.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setOpen((o) => !o)} data-testid={`expand-breaches-${item.theme_id}`}
            className="flex items-center gap-1 text-xs font-semibold text-red-600">
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {item.breaching_risks.length} risk{item.breaching_risks.length !== 1 ? "s" : ""} over appetite
          </button>
          {open && (
            <div className="mt-2 space-y-1.5">
              {item.breaching_risks.map((r) => (
                <div key={r.risk_id} className="flex items-center justify-between rounded border border-red-100 bg-red-50/50 px-3 py-1.5 text-sm">
                  <span className="text-ek-ink">{r.title}</span>
                  <span className="shrink-0 font-semibold text-red-600">residual {r.residual_score} <span className="text-xs">(+{r.over_by})</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && <Editor item={item} onSaved={() => { setEditing(false); onSaved(); }} onCancel={() => setEditing(false)} />}
    </div>
  );
};

export default function Appetite() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "risk.write");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/appetite").then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load risk appetite"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const s = data?.summary;
  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12" data-testid="appetite-page">
        <p className="ek-eyebrow">Risk Appetite & Tolerance</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">How much risk is acceptable</h1>
        <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
          Leadership sets the appetite and tolerance per theme — the worst acceptable residual score and the financial
          exposure ceiling. ERIO checks live exposure against it and flags every breach automatically.
        </p>

        {loading ? (
          <p className="py-24 text-center text-sm text-ek-ink/65">Assessing exposure against appetite…</p>
        ) : (
          <>
            {s && (
              <div className="mt-6 grid grid-cols-3 gap-4">
                <SummaryTile testid="summary-breached" count={s.breached} status="breached" />
                <SummaryTile testid="summary-approaching" count={s.approaching} status="approaching" />
                <SummaryTile testid="summary-within" count={s.within} status="within" />
              </div>
            )}
            <div className="mt-6 space-y-3">
              {(data?.items || []).map((item) => (
                <ThemeCard key={item.theme_id} item={item} canWrite={canWrite} onSaved={load} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
